import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import Step1 from '@/features/Application/components/steps/Step1';
import Step2 from '@/features/Application/components/steps/Step2';
import Step3 from '@/features/Application/components/steps/Step3';
import Step4 from '@/features/Application/components/steps/Step4';
import Step5 from '@/features/Application/components/steps/Step5';
import { cn } from '@/lib/utils';
import {
	VALIDATION_ERRORS,
	validateEmail,
	validatePhone,
	validateRequired
} from '@/services/validators';
import statusConfig from '../../config/status.json';

interface Errors {
	initials?: string;
	lastName?: string;
	phone?: string;
	email?: string;
	agreeToTerms?: string;
	agreeToCancel?: string;
	hasNoLegalIssues?: string;
	hasNoInsuranceIssues?: string;
	hasNoBankruptcyIssues?: string;
	[key: string]: string | undefined;
}

const Application = (): React.JSX.Element => {
	const { t } = useTranslation();
	const posthog = usePostHog();
	const { step, id, selected } = useParams<{ step?: string; id: string; selected: string }>();
	const navigate = useNavigate();
	const [isVisible, setIsVisible] = useState(false);
	const { applicationData, appData, applicationManagement } = useAppContext();
	// Start in loading state when id is present to avoid a flash of the form
	// before the fetch effect fires (which would cause DOM detachment in tests)
	const [isFetchingData, setIsFetchingData] = useState(!!id);
	const [fetchError, setFetchError] = useState<string | null>(null);

	// Stable reference to the fetch function to avoid re-running the effect
	const fetchApplicationData = applicationManagement.fetch;

	// Add validation state
	const [errors, setErrors] = useState<Errors>({});
	const [isStepValid, setIsStepValid] = useState(false);

	// Get current step from URL or default to 1
	const currentStep = step ? Number.parseInt(step, 10) : 1;

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [currentStep]);

	// Identify user with policy_upload_form_id from URL
	useEffect(() => {
		if (id) posthog?.identify(id);
	}, [id, posthog]);

	// Track if we've fetched application data for this ID
	const hasFetchedRef = useRef<string | null>(null);

	// Fetch pre-filled application data (name, email, phone from contact form) on mount
	useEffect(() => {
		if (id && hasFetchedRef.current !== id) {
			setIsFetchingData(true);
			setFetchError(null);
			fetchApplicationData(id)
				.then(() => {
					hasFetchedRef.current = id;
				})
				.catch((error) => {
					const message = error instanceof Error ? error.message : String(error);
					setFetchError(message);
				})
				.finally(() => {
					setIsFetchingData(false);
				});
		} else {
			setIsFetchingData(false);
		}
	}, [id, fetchApplicationData]);

	const retryFetch = useCallback(() => {
		if (!id) return;
		setFetchError(null);
		setIsFetchingData(true);
		fetchApplicationData(id)
			.then(() => {
				hasFetchedRef.current = id;
			})
			.catch((error) => {
				const message = error instanceof Error ? error.message : String(error);
				setFetchError(message);
			})
			.finally(() => {
				setIsFetchingData(false);
			});
	}, [id, fetchApplicationData]);

	// Navigation function
	const setStep = useCallback(
		(newStep: number) => {
			navigate(`/aanvraag/${id}/${selected}/${newStep}`);
		},
		[navigate, id, selected]
	);

	// Validation function for each step
	const validateStep = useCallback(() => {
		const newErrors: Errors = {};
		let valid: boolean;

		switch (currentStep) {
			case 1: {
				const initialsError = validateRequired(applicationData.initials || '');
				if (initialsError) {
					newErrors.initials =
						initialsError === VALIDATION_ERRORS.REQUIRED
							? t('application.steps.step3.validation.initialsRequired')
							: initialsError;
				}

				const lastNameError = validateRequired(applicationData.lastName || '');
				if (lastNameError) {
					newErrors.lastName =
						lastNameError === VALIDATION_ERRORS.REQUIRED
							? t('application.steps.step3.validation.lastNameRequired')
							: lastNameError;
				}

				valid = Object.keys(newErrors).length === 0;
				break;
			}
			case 2: {
				const phoneError = validatePhone(applicationData.phone || '');
				if (phoneError) {
					if (phoneError === VALIDATION_ERRORS.REQUIRED) {
						newErrors.phone = t('application.steps.step4.validation.phoneRequired');
					} else if (phoneError === VALIDATION_ERRORS.PHONE_INVALID) {
						newErrors.phone = t('application.steps.step4.validation.phoneInvalid');
					}
				}

				const emailError = validateEmail(applicationData.email || '');
				if (emailError) {
					if (emailError === VALIDATION_ERRORS.REQUIRED) {
						newErrors.email = t('application.steps.step4.validation.emailRequired');
					} else if (emailError === VALIDATION_ERRORS.EMAIL_INVALID) {
						newErrors.email = t('application.steps.step4.validation.emailInvalid');
					}
				}

				valid = Object.keys(newErrors).length === 0;
				break;
			}
			case 3:
				if (!applicationData.agreeToTerms)
					newErrors.agreeToTerms = 'Je moet akkoord gaan met de voorwaarden';
				if (!applicationData.agreeToCancel)
					newErrors.agreeToCancel = 'Je moet akkoord gaan met het royeren van polissen';

				valid = Object.keys(newErrors).length === 0;
				break;
			case 4:
				if (!applicationData.hasNoLegalIssues)
					newErrors.hasNoLegalIssues = 'Je moet akkoord gaan met de voorwaarden';
				if (!applicationData.hasNoInsuranceIssues)
					newErrors.hasNoInsuranceIssues = 'Je moet akkoord gaan met de voorwaarden';
				if (!applicationData.hasNoBankruptcyIssues)
					newErrors.hasNoBankruptcyIssues = 'Je moet akkoord gaan met de voorwaarden';

				valid = Object.keys(newErrors).length === 0;
				break;
			default:
				valid = true;
		}

		setErrors(newErrors);
		setIsStepValid(valid);
		return valid;
	}, [currentStep, applicationData, t]);

	useEffect(() => {
		if (
			currentStep !== 5 &&
			id &&
			selected &&
			appData?.status &&
			statusConfig.groups.isApplied.includes(appData.status)
		) {
			navigate(`/aanvraag/${id}/${selected}/5`, { replace: true });
		}
	}, [appData?.status, navigate, currentStep, id, selected]);

	// Validate on step or form data change
	useEffect(() => {
		validateStep();
		setTimeout(() => setIsVisible(true), 100);
	}, [validateStep]);

	// Redirect logic based on application state
	useEffect(() => {
		if (currentStep > 1 && currentStep !== 5 && applicationData.initials === null) {
			navigate(`/aanvraag/${id}/${selected}/1`, { replace: true });
		}
	}, [currentStep, navigate, id, selected, applicationData]);

	const [isSaving, setIsSaving] = useState(false);

	if (isFetchingData) {
		return (
			<section className="container mx-auto px-5 py-4 lg:py-14">
				<div className="w-full lg:w-11/12 xl:w-10/12 2xl:w-9/12 mx-auto mt-8 sm:mt-16 flex justify-center">
					<Spinner size="lg" visible />
				</div>
			</section>
		);
	}

	if (fetchError) {
		return (
			<section className="container mx-auto px-5 py-4 lg:py-14">
				<div className="w-full lg:w-11/12 xl:w-10/12 2xl:w-9/12 mx-auto mt-8 sm:mt-16 text-center">
					<p className="text-destructive mb-4">
						{t('application.fetchError', 'Er is iets misgegaan bij het laden van je gegevens.')}
					</p>
					<Button onClick={retryFetch}>{t('common.retry', 'Opnieuw proberen')}</Button>
				</div>
			</section>
		);
	}

	return (
		<section
			className={cn(
				'container mx-auto px-5 py-4 lg:py-14 transition-opacity duration-300',
				isVisible ? 'opacity-100' : 'opacity-0',
				isSaving && 'opacity-50 pointer-events-none'
			)}
		>
			<div className="w-full lg:w-11/12 xl:w-10/12 2xl:w-9/12 mx-auto mt-8 sm:mt-16">
				<Step1
					step={currentStep}
					setStep={setStep}
					isStepValid={isStepValid}
					errors={errors}
					isSaving={isSaving}
					setIsSaving={setIsSaving}
				/>
				<Step2
					step={currentStep}
					setStep={setStep}
					isStepValid={isStepValid}
					errors={errors}
					isSaving={isSaving}
					setIsSaving={setIsSaving}
				/>
				<Step3
					step={currentStep}
					setStep={setStep}
					isStepValid={isStepValid}
					errors={errors}
					isSaving={isSaving}
					setIsSaving={setIsSaving}
				/>
				<Step4
					step={currentStep}
					setStep={setStep}
					isStepValid={isStepValid}
					errors={errors}
					isSaving={isSaving}
					setIsSaving={setIsSaving}
				/>
				<Step5 step={currentStep} isSaving={isSaving} setIsSaving={setIsSaving} />
			</div>
		</section>
	);
};

export default Application;
